import {getErrorMessage} from '@/helpers/getErrorMessage'
import Modal from '@/shared-components/Modal/Modal'
import {useAppDispatch} from '@/store'
import {JourneyError} from '@/types/JourneyError'
import {getCountriesCatalogUseCase} from '@/useCases/getCountriesCatalogUseCase'
import {onboardCountryUseCase} from '@/useCases/onboardCountryUseCase'
import {showNotification} from '@ReduxActions'
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {Dropdown, DropdownItem} from 'pepsico-ds'
import {useMemo, useState} from 'react'
import {useIntl} from 'react-intl'
import {ONBOARDED_COUNTRIES_QUERY_KEY, useOnboardedCountries} from './hooks/useOnboardedCountries'

type AddCountryModalProps = {
  isOpen: boolean
  onClose: () => void
}

export const AddCountryModal = ({isOpen, onClose}: AddCountryModalProps) => {
  const {formatMessage} = useIntl()
  const dispatch = useAppDispatch()
  const queryClient = useQueryClient()
  const [selectedCountry, setSelectedCountry] = useState<DropdownItem>()

  const {data: onboardedCountries} = useOnboardedCountries()
  const {
    data: catalog,
    isFetching: isLoadingCatalog,
    error: catalogError,
  } = useQuery({
    queryKey: ['countriesCatalog'],
    queryFn: ({signal}) => getCountriesCatalogUseCase(signal),
    enabled: isOpen,
    // the catalog is a static list on the backend
    staleTime: Infinity,
  })

  const {mutate, isPending: isOnboarding} = useMutation({
    mutationKey: ['onboardCountry'],
    mutationFn: onboardCountryUseCase,
  })

  const countryOptions = useMemo<DropdownItem[]>(() => {
    const onboardedCodes = new Set(onboardedCountries?.map(country => country.code))
    return (catalog ?? [])
      .filter(country => !onboardedCodes.has(country.code))
      .map(country => ({id: country.code, displayText: country.name}))
  }, [catalog, onboardedCountries])

  const hasNoOptions = !isLoadingCatalog && !catalogError && countryOptions.length === 0

  const helperText = catalogError
    ? formatMessage({id: 'MARKETS.ADD_COUNTRY_CATALOG_ERROR'})
    : hasNoOptions
    ? formatMessage({id: 'MARKETS.ADD_COUNTRY_NO_OPTIONS'})
    : undefined

  const close = () => {
    setSelectedCountry(undefined)
    onClose()
  }

  const onCountryDidChange = (values?: DropdownItem[]) => {
    setSelectedCountry(values?.[0])
  }

  const onConfirm = () => {
    if (!selectedCountry || isOnboarding) return

    mutate(
      {code: selectedCountry.id},
      {
        onSuccess: () => {
          queryClient.invalidateQueries({queryKey: ONBOARDED_COUNTRIES_QUERY_KEY})
          dispatch(
            showNotification({
              show: true,
              type: 'success',
              title: formatMessage({id: 'MARKETS.ADD_COUNTRY_SUCCESS_TITLE'}),
              message: formatMessage(
                {id: 'MARKETS.ADD_COUNTRY_SUCCESS_MESSAGE'},
                {country: selectedCountry.displayText}
              ),
            })
          )
          close()
        },
        onError: error => {
          const isCustomError = (error as JourneyError) instanceof JourneyError
          const {title, message} = getErrorMessage(error)
          dispatch(
            showNotification({
              show: true,
              type: isCustomError ? 'warning' : 'error',
              title,
              message,
            })
          )
        },
      }
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      title={formatMessage({id: 'MARKETS.ADD_COUNTRY_MODAL_TITLE'})}
      primaryButtonText={formatMessage({id: 'MARKETS.ADD_COUNTRY_MODAL_CONFIRM'})}
      primaryButtonDisabled={!selectedCountry || isOnboarding}
      onClickPrimaryButton={onConfirm}
      onClickSecondaryButton={close}
      onClose={close}
      // let the dropdown menu overflow the modal body instead of being clipped by its scroll container
      className="[&_.model-body-slot-view]:!overflow-visible"
      classNameWrapper="w-[400px] overflow-visible"
      content={
        <div data-testid="add-country-modal-content">
          <Dropdown
            data-testid="add-country-dropdown"
            data-cy="add-country-dropdown"
            label={formatMessage({id: 'MARKETS.ADD_COUNTRY_DROPDOWN_LABEL'})}
            selection={isLoadingCatalog ? 'loading' : 'single'}
            childList={countryOptions}
            // pepsico-ds does not render the selected value for single selection; show it as placeholder
            placeholder={
              selectedCountry?.displayText ?? formatMessage({id: 'MARKETS.ADD_COUNTRY_DROPDOWN_PLACEHOLDER'})
            }
            selectedValue={selectedCountry ? [selectedCountry] : []}
            setSelectedValue={onCountryDidChange}
            disabled={isLoadingCatalog || !!catalogError || hasNoOptions || isOnboarding}
            error={!!catalogError}
            helperText={helperText}
            isRequired
          />
        </div>
      }
    />
  )
}
